/**
 * RealtimeFunctionRouter — translates OpenAI-Realtime function-calls (Content
 * #1196) into the audio-guide's existing internal calls.
 *
 * The realtime voice-guide is "our guide", not a bare voice bot: the model
 * speaks over the WebRTC audio stream AND drives the existing Topic-Card /
 * Gallery / Knowledge / Map UI via function-calls. This router is the single
 * place that classifies an incoming call and dispatches it:
 *
 *   - **Display-Hint** (`show_topic`, `show_image`, `show_knowledge_pin`,
 *     `focus_map`) → applied LOCALLY to the existing UI via the injected
 *     {@link RealtimeVisualAdapter}. No backend round-trip, zero latency.
 *   - **Read** (`knowledge_query`, `pois_near`, `narration_near`, …) and
 *     **Persist** (`persist_narration`) → returned as a {@link RealtimeBackendRoute}
 *     for the client transport to POST (stamping the `X-Session-ID` header).
 *
 * SRP: this router *translates* the OpenAI wire-protocol; it does NOT decide
 * which mode is active (that's the mode-switcher) and it NEVER speaks — realtime
 * audio comes from the WebRTC stream, not TTS. Kept decoupled from AudioGuide
 * internals via the narrow adapter interface so it's unit-testable without a
 * browser or a live WebRTC session.
 */
// Tool-name classification — the Content #1196 contract.
const DISPLAY_TOOLS = new Set(['show_topic', 'show_image', 'show_knowledge_pin', 'focus_map']);
const AIAPI_READ_TOOLS = new Set([
    'knowledge_query',
    'knowledge_get',
    'osm_nearby',
    'pois_near',
    'routes_list',
    'route_pretty',
]);
const GUIDE_API_READ_TOOLS = new Set(['narration_near']);
const GUIDE_API_PERSIST_TOOLS = new Set(['persist_narration']);
/** Coerce a possibly-stringified JSON number into a finite number, else undefined. */
function num(v) {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
    return Number.isFinite(n) ? n : undefined;
}
export class RealtimeFunctionRouter {
    constructor(_visual) {
        this._visual = _visual;
    }
    /**
     * Classify + handle one Realtime function-call. Display-Hints are applied
     * synchronously via the adapter and return `{kind:'display'}`; Read/Persist
     * return a backend descriptor for the client transport to POST. Never speaks.
     */
    route(call) {
        const name = call?.name ?? '';
        const args = (call?.arguments ?? {});
        if (DISPLAY_TOOLS.has(name)) {
            this._applyDisplay(name, args);
            return { kind: 'display', tool: name };
        }
        if (AIAPI_READ_TOOLS.has(name)) {
            return { kind: 'backend', target: 'aiapi', tool: name, arguments: args, feedback: 'searching' };
        }
        if (GUIDE_API_READ_TOOLS.has(name)) {
            return { kind: 'backend', target: 'guide-api', tool: name, arguments: args, feedback: 'searching' };
        }
        if (GUIDE_API_PERSIST_TOOLS.has(name)) {
            return { kind: 'backend', target: 'guide-api', tool: name, arguments: args, feedback: 'none' };
        }
        return { kind: 'unknown', tool: name };
    }
    /** Map a Display-Hint onto the existing UI. Bad/missing args → no-op (graceful). */
    _applyDisplay(name, args) {
        switch (name) {
            case 'show_topic': {
                const title = typeof args.title === 'string' ? args.title : '';
                if (!title)
                    return;
                this._visual.showTopic({ title, lat: num(args.lat), lon: num(args.lon) });
                return;
            }
            case 'show_image': {
                const id = num(args.storage_id);
                if (id === undefined)
                    return;
                this._visual.showImages([this._visual.mediaUrlForStorageId(id)]);
                return;
            }
            case 'show_knowledge_pin': {
                const kid = num(args.knowledge_post_id);
                if (kid === undefined)
                    return;
                this._visual.showKnowledgePin(kid);
                return;
            }
            case 'focus_map': {
                const lat = num(args.lat);
                const lon = num(args.lon);
                if (lat === undefined || lon === undefined)
                    return;
                this._visual.focusMap({ lat, lon, zoom: num(args.zoom) });
                return;
            }
        }
    }
}
//# sourceMappingURL=RealtimeFunctionRouter.js.map